import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { LineReveal } from "@/components/Reveal";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 30, damping: 20 });
  const sy = useSpring(my, { stiffness: 30, damping: 20 });
  const orbX = useTransform(sx, [-1, 1], [-26, 26]);
  const orbY = useTransform(sy, [-1, 1], [-18, 18]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grain"
    >
      <motion.div
        aria-hidden="true"
        style={{ x: orbX, y: orbY }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] max-w-[820px] aspect-square"
      >
        <div className="w-full h-full anim-breathe">
          <div className="w-full h-full rounded-full anim-orb bg-[radial-gradient(circle_at_42%_38%,rgba(76,144,128,0.16),rgba(216,208,195,0.14)_45%,transparent_70%)] blur-2xl" />
        </div>
      </motion.div>
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -right-32 w-[42vw] max-w-[520px] aspect-square rounded-full bg-[radial-gradient(circle,rgba(36,95,90,0.08),transparent_65%)] blur-2xl"
      />

      <div className="relative z-10 mx-auto max-w-4xl px-6 pt-32 pb-24 text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.1 }}
          className="eyebrow mb-10"
          data-testid="hero-eyebrow"
        >
          Tara Aurora
        </motion.p>

        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.08] tracking-tight text-charcoal" data-testid="hero-headline">
          <LineReveal
            lines={["You were never meant", "to live in", "survival mode."]}
            lineClassName="last:text-deepteal"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.0 }}
          className="mt-9 text-base md:text-lg leading-relaxed text-taupe max-w-xl mx-auto"
          data-testid="hero-subcopy"
        >
          Come back to your body. Understand what it’s been trying to tell you.
          Learn to live with more safety, steadiness, and choice.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.25 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/regulate"
            data-testid="hero-discover-regulate-button"
            className="w-full sm:w-auto bg-teal text-ivory text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-deepteal transition-colors duration-500"
          >
            Discover REGULATE
          </Link>
          <Link
            to="/begin-here"
            data-testid="hero-begin-here-button"
            className="w-full sm:w-auto border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-teal hover:text-deepteal transition-colors duration-500"
          >
            Begin Here
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
